import React, { useState } from 'react';
import { NewsItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { Search, ArrowRight, X, Share2, Printer, ChevronLeft, ChevronRight } from 'lucide-react';

interface NewsViewProps {
  currentLang: LanguageCode;
  news: NewsItem[];
  selectedNewsItem?: NewsItem | null;
  onClearSelectedNews?: () => void;
  onSelectNewsItem?: (item: NewsItem) => void;
}

export const NewsView: React.FC<NewsViewProps> = ({
  currentLang,
  news,
  selectedNewsItem,
  onClearSelectedNews,
  onSelectNewsItem,
}) => {
  const t = translations[currentLang];
  const [selectedCategory, setSelectedCategory] = useState<string>('Tüm');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeArticle, setActiveArticle] = useState<NewsItem | null>(selectedNewsItem || null);
  const [shareToast, setShareToast] = useState(false);

  const handleShare = (titleText?: string) => {
    if (navigator.share) {
      navigator.share({
        title: titleText || t.newsMainTitle,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
    }
  };

  const rawCategories = ['Tüm', 'Duyurular', 'Dernek Haberleri', 'Kültür & Sanat', 'Basın Açıklamaları'];

  const filtered = news.filter((item) => {
    const matchesCat = selectedCategory === 'Tüm' || item.category === selectedCategory;
    const titleText = getLocalized(item.title, currentLang);
    const summaryText = getLocalized(item.summary, currentLang);
    const matchesQuery =
      titleText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      summaryText.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesQuery;
  });

  // Previous & Next News navigation
  const currentIndex = activeArticle ? news.findIndex((n) => n.id === activeArticle.id) : -1;
  const handlePrevArticle = () => {
    if (currentIndex > 0) {
      const prev = news[currentIndex - 1];
      setActiveArticle(prev);
      if (onSelectNewsItem) onSelectNewsItem(prev);
    }
  };
  const handleNextArticle = () => {
    if (currentIndex >= 0 && currentIndex < news.length - 1) {
      const next = news[currentIndex + 1];
      setActiveArticle(next);
      if (onSelectNewsItem) onSelectNewsItem(next);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
          {t.newsBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.newsMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.newsSubtitle}
        </p>
      </div>

      {/* Filter and Search */}
      <div className="glass-card rounded-2xl p-4 border border-[#38BDF8]/30 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
          {rawCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-[#38BDF8] text-[#0B1426] font-bold shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                  : 'bg-[#0B1426] text-gray-300 hover:bg-[#132238] border border-[#38BDF8]/20'
              }`}
            >
              {getLocalizedCategory(cat, currentLang)}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-[#38BDF8]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchNews}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs placeholder-gray-400 focus:outline-none focus:border-[#38BDF8] focus:shadow-[0_0_10px_rgba(56,189,248,0.4)]"
          />
        </div>
      </div>

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="glass-card rounded-3xl overflow-hidden border border-[#38BDF8]/30 flex flex-col justify-between group hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-all duration-300"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={item.image}
                alt={getLocalized(item.title, currentLang)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#0B1426]/90 backdrop-blur-md text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/40 shadow-[0_0_8px_rgba(56,189,248,0.4)]">
                {getLocalizedCategory(item.category, currentLang)}
              </span>
            </div>

            <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
              <div>
                <span className="text-[10px] text-[#7DD3FC] font-medium block mb-1">
                  📅 {item.date} • ✍️ {item.author}
                </span>
                <h3 className="text-base font-bold text-white group-hover:text-[#38BDF8] transition-colors line-clamp-2">
                  {getLocalized(item.title, currentLang)}
                </h3>
                <p className="text-xs text-gray-300 line-clamp-3 mt-2 leading-relaxed">
                  {getLocalized(item.summary, currentLang)}
                </p>
              </div>

              <div className="pt-2 border-t border-[#38BDF8]/15 flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="text-[9px] px-2 py-0.5 rounded-md bg-[#0B1426] border border-[#38BDF8]/20 text-[#7DD3FC]">
                      #{tag}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setActiveArticle(item);
                    if (onSelectNewsItem) onSelectNewsItem(item);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#38BDF8] hover:text-[#A5F3FC] transition-colors"
                >
                  <span>{t.readMore}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Share Toast Banner */}
      {shareToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#38BDF8] text-[#0B1426] px-5 py-3 rounded-2xl font-extrabold text-xs shadow-[0_0_20px_rgba(56,189,248,0.8)] flex items-center gap-2 border border-white animate-bounce">
          <Share2 className="w-4 h-4 stroke-[3]" />
          <span>Bağlantı kopyalandı!</span>
        </div>
      )}

      {/* Full Article Overlay (Clean, Flat, Fixed Header with Geri/İleri/Kapat & Oval Glowing Image) */}
      {activeArticle && (
        <div className="fixed inset-0 z-50 bg-[#0B1426] text-white flex flex-col overflow-hidden animate-in fade-in duration-200">
          {/* Fixed Top Bar (Sabit Üst Menü) */}
          <div className="sticky top-0 z-30 bg-[#0B1426]/95 backdrop-blur-md px-3 sm:px-6 py-3 border-b border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.25)] flex items-center justify-between">
            {/* Left: Geri (Back / Previous) */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevArticle}
                disabled={currentIndex <= 0}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex <= 0
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Geri</span>
              </button>

              <button
                onClick={handleNextArticle}
                disabled={currentIndex >= news.length - 1}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex >= news.length - 1
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <span className="hidden sm:inline">İleri</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Center: Category Badge */}
            <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/50 shadow-[0_0_10px_rgba(56,189,248,0.4)] truncate max-w-[150px] sm:max-w-xs">
              {getLocalizedCategory(activeArticle.category, currentLang)}
            </span>

            {/* Right: Kapat (Close) */}
            <button
              onClick={() => {
                setActiveArticle(null);
                if (onClearSelectedNews) onClearSelectedNews();
              }}
              className="px-3.5 py-1.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center gap-1"
            >
              <span>{t.close || 'Kapat'}</span>
              <X className="w-4 h-4 stroke-[3]" />
            </button>
          </div>

          {/* Scrollable Article Body (Düz, Çerçevesiz, Sağda ve Solda 0.5 Boşluk) */}
          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-6 max-w-4xl mx-auto w-full space-y-6 scrollbar-thin">
            {/* Oval Glowing Image Frame (Buz Mavisi Işıklı Oval Çerçeve) */}
            <div className="relative w-full max-h-[380px] rounded-[2.5rem] overflow-hidden border-2 border-[#38BDF8] shadow-[0_0_25px_rgba(56,189,248,0.5)]">
              <img
                src={activeArticle.image}
                alt="News Detail"
                className="w-full h-full object-cover max-h-[380px]"
              />
            </div>

            {/* Title & Metadata */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-3 text-xs text-[#38BDF8] font-bold">
                <span>📅 {activeArticle.date}</span>
                <span>•</span>
                <span>✍️ {activeArticle.author}</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading leading-tight">
                {getLocalized(activeArticle.title, currentLang)}
              </h2>
            </div>

            {/* Article Text Content (Düz, Çerçevesiz) */}
            <div className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-line py-2">
              {getLocalized(activeArticle.content, currentLang)}
            </div>

            {/* Bottom Actions: Paylaş & Yazdır (Haberin Bittiği Yerde En Altta) */}
            <div className="pt-8 pb-12 border-t border-[#38BDF8]/20 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare(getLocalized(activeArticle.title, currentLang))}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8] text-[#38BDF8] font-extrabold text-xs hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all shadow-[0_0_12px_rgba(56,189,248,0.4)] flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Paylaş</span>
                </button>

                <button
                  onClick={() => window.print()}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8]/50 text-gray-200 font-bold text-xs hover:bg-[#1C3352] hover:text-white transition-all flex items-center gap-2"
                >
                  <Printer className="w-4 h-4 text-[#38BDF8]" />
                  <span>Yazdır</span>
                </button>
              </div>

              <button
                onClick={() => {
                  setActiveArticle(null);
                  if (onClearSelectedNews) onClearSelectedNews();
                }}
                className="px-6 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all"
              >
                {t.close || 'Kapat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

